// ══════════════════════════════════════════════════════════════════
// CartHub Dashboard — Modulo condiviso
// Contiene: API client, CSS, icone SVG, dati mock, helper functions.
// Importato da tutti i componenti della dashboard.
// ══════════════════════════════════════════════════════════════════

import { useState, useEffect } from "react";
import i18n, { formatEur, formatAgo } from "./i18n";

// ─── API client ───
export const API_BASE = "https://app.carthub.cloud/api";
export const api = async (endpoint, options = {}) => {
  const token = localStorage.getItem("carthub_token");
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...options,
  });
  if (res.status === 401 && token) { localStorage.removeItem("carthub_token"); window.location.href = "/"; return {}; }
  if (res.status === 429) { return res.json(); }
  return res.json();
};

/**
 * Come `api` ma lancia se la risposta non è OK (utile per salvataggi dove serve feedback errore).
 * 401 con token: redirect login (come api). 429: ritorna il body senza throw.
 */
export async function apiRequest(endpoint, options = {}) {
  const token = localStorage.getItem("carthub_token");
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && token) {
    localStorage.removeItem("carthub_token");
    window.location.href = "/";
    return undefined;
  }
  if (res.status === 429) return data;
  if (!res.ok) {
    const msg =
      (typeof data.error === "string" && data.error) ||
      (typeof data.message === "string" && data.message) ||
      `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// ─── CSS (specchio 1:1 di admin.css) ───
export const styles = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --font: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  --text:          #1a1a1a;
  --text2:         #6b7280;
  --text3:         #9ca3af;
  --border:        rgba(26, 26, 26, 0.08);
  --border2:       rgba(26, 26, 26, 0.12);
  --canvas:        #FBFDF7;
  --surface-panel: #ffffff;
  --surface-bright:#ffffff;
  --bg:            var(--canvas);
  --surface:       var(--surface-bright);
  --radius-module: 18px;
  --gap-module:    8px;
  --shadow-module: 0 5px 10px rgba(26, 26, 26, 0.05);
  --shadow-drawer: 0 5px 10px rgba(26, 26, 26, 0.05);
  --accent:        #0A85CF;
  --accent-hover:  #0878b8;
  --link:          #0A85CF;
  --accent-bg:     #e8f4fc;
  --accent-border: #b8daf2;
  --accent-glow:   rgba(10,133,207,0.18);
  --green:         #2e7d32;
  --green-bg:      #f0faf0;
  --green-border:  #c8e6c9;
  --red:           #c62828;
  --red-bg:        #fff5f5;
  --red-border:    #ffcdd2;
  --yellow:        #e65100;
  --yellow-bg:     #fff8f0;
  --yellow-border: #ffe0b2;
}

html, body, #root {
  height: 100%;
  font-family: var(--font);
  font-size: 15px;
  color: var(--text);
  background: var(--bg);
  -webkit-font-smoothing: antialiased;
}

.content a:not(.ch-btn):not(.ch-btn-primary) {
  color: var(--link);
  text-decoration: none;
}
.content a:not(.ch-btn):not(.ch-btn-primary):hover {
  color: var(--accent-hover);
  text-decoration: underline;
}

::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }

/* Schermata “Aggiornamento piano…” (App.js) */
.app-loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 16px;
  background: var(--canvas);
  color: var(--text2);
  font-size: 15px;
  font-weight: 500;
  font-family: var(--font);
}
.app-loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(26, 26, 26, 0.08);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: ch-spin 0.8s linear infinite;
}
@keyframes ch-spin { to { transform: rotate(360deg); } }

/* ══ LAYOUT ══ */
.app { display: flex; height: 100vh; overflow: hidden; background: var(--canvas); box-sizing: border-box; }

.sidebar-backdrop {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(26, 26, 26, 0.28);
  z-index: 180;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.sidebar-backdrop.is-open {
  display: block;
  opacity: 1;
  pointer-events: auto;
}

.mobile-nav-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  margin-right: 10px;
  margin-left: -4px;
  border: none;
  border-radius: 12px;
  background: var(--surface-bright);
  color: var(--text);
  cursor: pointer;
  box-shadow: var(--shadow-module);
  flex-shrink: 0;
  transition: color 0.15s, box-shadow 0.15s;
}
.mobile-nav-toggle:hover { color: var(--accent); }

/* ══ SIDEBAR ══ */
.sidebar {
  width: 290px;
  margin: var(--gap-module);
  background: var(--surface-panel);
  border: none;
  border-radius: var(--radius-module);
  box-shadow: var(--shadow-module);
  display: flex; flex-direction: column;
  flex-shrink: 0;
  overflow-x: hidden;
  overflow-y: auto;
}
.sidebar-head {
  padding: 18px 16px 14px;
  border-bottom: 1px solid rgba(26, 26, 26, 0.06);
}
.sidebar-logo { font-size: 19px; font-weight: 700; letter-spacing: -0.02em; color: var(--text); }
.sidebar-logo span { color: var(--accent); }
.sidebar-sub { font-size: 13px; color: var(--text3); margin-top: 2px; }

.sidebar-nav { padding: 10px 8px; flex: 1; }
.nav-section { font-size: 13px; font-weight: 600; color: var(--text3); letter-spacing: .05em; text-transform: uppercase; padding: 10px 8px 4px; }

.nav-item {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; border-radius: 6px;
  cursor: pointer; color: var(--text2); font-size: 15px; font-weight: 500;
  transition: all .15s; margin-bottom: 1px;
  border: 1px solid transparent; user-select: none;
}
.nav-item:hover { background: var(--bg); color: var(--text); }
.nav-item.active { background: var(--accent-bg); color: var(--accent); border-color: var(--accent-border); box-shadow: none; }
.nav-item svg { width: 15px; height: 15px; flex-shrink: 0; }
.nav-badge { margin-left: auto; background: var(--accent); color: #fff; font-size: 13px; font-weight: 600; padding: 1px 6px; border-radius: 20px; }

/* Sidebar — gruppo Statistiche (sottomenu) */
.nav-group { margin-bottom: 2px; }
.nav-item--parent {
  display: flex; align-items: center; gap: 8px;
  width: 100%; box-sizing: border-box;
}
.nav-item--parent .nav-parent-label { flex: 1; min-width: 0; text-align: left; }
.nav-group-chevron {
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; margin-left: 4px; opacity: 0.65; color: var(--text3);
  transition: transform .2s ease;
}
.nav-item--parent.is-open .nav-group-chevron { transform: rotate(180deg); opacity: 0.85; }
.nav-item--parent.has-active-child:not(.is-open) {
  color: var(--accent);
  border-color: var(--accent-border);
  background: var(--accent-bg);
}
.nav-submenu { padding: 2px 0 6px 0; }
.nav-item--sub {
  padding-left: 36px;
  font-size: 14px;
  font-weight: 500;
}
.nav-item--sub .nav-badge { margin-left: auto; }

.sidebar-foot { padding: 12px; border-top: 1px solid rgba(26, 26, 26, 0.06); }
.merchant-row { display: flex; align-items: center; gap: 10px; }
.merchant-av { width: 28px; height: 28px; border-radius: 6px; background: var(--accent-bg); border: 1px solid var(--accent-border); display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600; color: var(--accent); flex-shrink: 0; }
.merchant-name { font-size: 13px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.merchant-domain { font-size: 13px; color: var(--text3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.logout-btn { margin-left: auto; background: none; border: none; cursor: pointer; color: var(--text3); padding: 4px; border-radius: 4px; transition: color .15s; flex-shrink: 0; }
.logout-btn:hover { color: var(--red); }

/* ══ MAIN ══ */
.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--canvas);
  min-width: 0;
}
.topbar {
  min-height: 56px;
  margin: var(--gap-module);
  background: var(--surface-panel);
  border: none;
  border-radius: var(--radius-module);
  box-shadow: var(--shadow-module);
  display: flex;
  align-items: center;
  padding: 0 20px;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
}
.topbar-title { font-size: 15px; font-weight: 600; flex: 1; min-width: 0; }

/* Banner app (silent / pausa email) — stesso linguaggio di card/shell */
.ch-app-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 var(--gap-module) var(--gap-module); /* margin: 0 var(--gap-module) var(--gap-module);*/
  padding: 12px 18px;
  font-size: 13px;
  line-height: 1.45;
  border-radius: var(--radius-module);
  box-shadow: var(--shadow-module);
  border: none;
  flex-shrink: 0;
}
.ch-app-banner__icon { font-size: 16px; flex-shrink: 0; }
.ch-app-banner__body { flex: 1; min-width: 200px; }
.ch-app-banner__title { font-weight: 600; margin-right: 8px; }
.ch-app-banner__btn {
  padding: 6px 14px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  font-family: var(--font);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
}

.ch-app-banner--silent {
  background: var(--surface-bright);
  color: #0284c7;
  box-shadow: var(--shadow-module);
}
.ch-app-banner--silent .ch-app-banner__title { color: #0369a1; }
.ch-app-banner--silent .ch-app-banner__btn {
  border-color: #0284c7;
  color: #0284c7;
}
.ch-app-banner--silent .ch-app-banner__btn:hover { background: #f0f9ff; }
.ch-app-banner__btn svg { width: 14px; height: 14px; flex-shrink: 0; }

.ch-app-banner--pause {
  background: var(--surface-bright);
  color: #ea580c;
  box-shadow: var(--shadow-module);
}
.ch-app-banner--pause .ch-app-banner__title { color: #c2410c; }
.ch-app-banner--pause .ch-app-banner__btn {
  border-color: #ea580c;
  color: #ea580c;
}
.ch-app-banner--pause .ch-app-banner__btn:hover { background: #fff7ed; }

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--gap-module);
  min-width: 0;
}

@media (max-width: 900px) {
  .mobile-nav-toggle { display: inline-flex; }
  .sidebar {
    position: fixed;
    left: var(--gap-module);
    top: var(--gap-module);
    bottom: var(--gap-module);
    z-index: 200;
    width: min(290px, calc(100vw - 16px));
    margin: 0;
    transform: translateX(calc(-100% - var(--gap-module)));
    transition: transform 0.22s ease;
    box-shadow: var(--shadow-module);
  }
  .sidebar.is-open {
    transform: translateX(0);
    box-shadow: var(--shadow-drawer);
  }
  .topbar {
    padding-left: 12px;
    padding-right: 12px;
  }
  .topbar-title {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

@media (max-width: 600px) {
  .stats-grid { grid-template-columns: 1fr !important; }
}

/* ══ PLUGIN CLASSES (1:1 da admin.css) ══ */

/* ch-tabs — scroll orizzontale su narrow (Settings, Brands) */
.ch-tabs { display: flex; gap: 0; border-bottom: 1px solid #e5e5e5; margin-bottom: 24px; overflow-x: auto; -webkit-overflow-scrolling: touch; flex-wrap: nowrap; scrollbar-width: thin; }
.ch-tab { flex-shrink: 0; padding: 10px 20px; font-size: 15px; font-weight: 500; color: #6b7280; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color .15s, border-color .15s; }
.ch-tab:hover { color: #1a1a1a; }
.ch-tab.active { color: var(--accent); border-bottom-color: var(--accent); }

/* ch-card */
.ch-card { background: var(--surface-panel); border: none; border-radius: var(--radius-module); padding: 20px 22px; margin-bottom: var(--gap-module); box-shadow: var(--shadow-module); }
.ch-card h2 { font-size: 15px; font-weight: 600; color: #1a1a1a; margin: 0 0 6px 0; }
.ch-card p { font-size: 15px; color: #6b7280; margin: 0 0 20px 0; line-height: 1.6; }
/* Anteprima widget Price Alert (PriceAlertSetup): .ch-card p non deve forzare grigio — eredita color dal box anteprima */
.ch-card .ch-pa-widget-preview p {
  color: inherit;
  margin: 0;
  line-height: 1.5;
  font-size: inherit;
}
.ch-card .ch-pa-widget-preview p.ch-pa-widget-preview__title {
  font-size: 15px;
  font-weight: 500;
  margin: 0 0 8px 0;
}
.ch-card .ch-pa-widget-preview p.ch-pa-widget-preview__footer {
  font-size: 13px;
  margin: 8px 0 0 0;
  opacity: 0.78;
}
.ch-card .ch-pa-widget-preview p.ch-pa-widget-preview__success {
  font-size: 14px;
  margin: 10px 0 0 0;
}
/* Icone anteprima / picker Price Alert: stroke visibile (currentColor) */
.ch-pa-widget-preview svg,
.ch-pa-icon-btn svg {
  display: block;
  color: inherit;
  flex-shrink: 0;
}
.ch-pa-icon-btn {
  color: var(--text);
}
/* Anteprima widget / popup: colonna destra sticky su desktop */
@media (min-width: 900px) {
  .ch-dashboard-sticky-preview {
    position: sticky;
    top: 88px;
    align-self: start;
    max-height: calc(100vh - 100px);
    overflow: auto;
  }
}
.ch-pa-save-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 0;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: var(--green-bg);
  color: var(--green);
  border: 1px solid var(--green-border);
}
.ch-pa-save-banner svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}
.ch-pa-save-banner[role="status"] {
  outline: none;
}
.ch-pa-save-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--green);
  font-size: 14px;
  font-weight: 500;
}
.ch-pa-save-inline svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}
.ch-pa-save-error {
  margin: 0 0 16px 0;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 14px;
  background: var(--red-bg);
  color: var(--red);
  border: 1px solid var(--red-border);
}
/* Card solo contenitore tabella (es. Carrelli): padding laterale su mobile per area scroll */
.ch-card-table-shell { padding: 0 !important; overflow: hidden; }
@media (max-width: 640px) {
  .ch-card-table-shell .table-wrap.table-min-wide { padding-left: 8px; padding-right: 8px; box-sizing: border-box; }
}

/* ch-btn */
.ch-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; font-size: 15px; font-weight: 500; border-radius: 6px; border: 1px solid #e5e5e5; background: #ffffff; color: #1a1a1a; cursor: pointer; transition: all .15s; font-family: var(--font); white-space: nowrap; }
.ch-btn:hover { background: #f9f9f9; border-color: #d1d1d1; }
.ch-btn-primary { background: #1a1a1a; color: #ffffff; border-color: #1a1a1a; }
.ch-btn-primary:hover { background: #333; border-color: #333; }
.ch-btn-sm { padding: 5px 10px; font-size: 13px; }
.ch-btn:disabled { opacity: .5; cursor: not-allowed; }
.ch-btn-ghost { background: transparent; border-color: transparent; color: var(--text2); }
.ch-btn-ghost:hover { background: var(--bg); color: var(--text); border-color: transparent; }

/* ch-field */
.ch-field { margin-bottom: 20px; }
.ch-field label { display: block; font-size: 15px; font-weight: 500; color: #1a1a1a; margin-bottom: 6px; }
.ch-input, .ch-select {
  width: 100%; max-width: 480px; padding: 8px 12px; font-size: 15px; font-family: var(--font);
  border: 1px solid rgba(26, 26, 26, 0.08); border-radius: 12px; color: #1a1a1a; background: var(--surface-bright);
  transition: border-color .15s; outline: none;
}
.ch-input:focus, .ch-select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-glow); }
.ch-input.full, .ch-select.full { max-width: 100%; }
.ch-input-sm { max-width: 90px; padding: 6px 10px; }
.ch-hint { font-size: 13px; color: #6b7280; margin-top: 6px; }

/* ch-info */
.ch-info { background: var(--accent-bg); border: 1px solid var(--accent-border); border-radius: 6px; padding: 12px 16px; font-size: 15px; color: #6b7280; margin-bottom: 20px; line-height: 1.6; }
.ch-info strong { color: #1a1a1a; }

/* Pannello help merchant — legenda carrelli, parametri popup, … (desktop orizzontale, mobile collassabile) */
.ch-help-legend {
  background: var(--surface-bright);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(26, 26, 26, 0.06);
  border: 1px solid rgba(26, 26, 26, 0.06);
  overflow: hidden;
  margin-bottom: 16px;
}
.ch-help-legend.ch-help-legend--embed { margin-bottom: 0; }
/* Carrello funnel — 3 colonne: copertura (torta 2 fette) | fasi (% prima) | sintesi */
.ch-funnel-coverage-split {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 14px 16px;
  margin-top: 4px;
}
.ch-funnel-coverage-split__body {
  flex: 1 1 0;
  min-width: 0;
}
.ch-funnel-coverage-split__body .ch-funnel-metrics-lead {
  margin-top: 0;
}
.ch-funnel-coverage-split__pie {
  align-self: center;
}
.ch-funnel-top-cols {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 10px;
  padding: 14px 18px 16px;
  box-sizing: border-box;
}
.ch-funnel-top-cols > .ch-funnel-col {
  flex: 1 1 0;
  min-width: 0;
  border: 1px solid rgba(26, 26, 26, 0.06);
  border-radius: 12px;
  padding: 12px 10px 14px;
  background: var(--surface-bright);
  box-sizing: border-box;
}
.ch-funnel-col--stages-split {
  flex: 1.45 1 0;
  min-width: min(100%, 260px);
}
.ch-funnel-stage-split {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 14px 18px;
  margin-top: 4px;
}
.ch-funnel-stage-split__pie {
  width: clamp(104px, 22vw, 132px);
  height: clamp(104px, 22vw, 132px);
  border-radius: 50%;
  border: none;
  outline: none;
  box-shadow: none;
  flex-shrink: 0;
  align-self: center;
}
.ch-funnel-stage-split--legend-only {
  flex-direction: column;
  width: 100%;
  margin-top: 4px;
  gap: 14px;
}
.ch-funnel-stage-split__row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
}
.ch-funnel-stage-split__row--pct-first {
  align-items: flex-start;
}
.ch-funnel-stage-split__pct-start {
  flex-shrink: 0;
  min-width: 2.75rem;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.45;
}
.ch-funnel-stage-split__pct-start--cart {
  color: #0a85cf;
}
.ch-funnel-stage-split__pct-start--checkout {
  color: #0d9488;
}
.ch-funnel-stage-split__pct-start--other {
  color: #64748b;
}
.ch-funnel-stage-split__line-text {
  margin: 0;
  flex: 1 1 0;
  min-width: 0;
  font-size: 12px;
  line-height: 1.55;
  color: #6b7280;
}
.ch-funnel-stage-split__line-text strong {
  color: #1a1a1a;
  font-weight: 700;
}
.ch-funnel-stage-split__line-count {
  color: #64748b;
  font-weight: 600;
  white-space: nowrap;
}
.ch-funnel-col--coverage .ch-funnel-stack-meta {
  flex-direction: column;
  gap: 6px;
}
.ch-funnel-col__desc--insight {
  margin-bottom: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #4b5563;
}
@media (max-width: 899px) {
  .ch-funnel-top-cols {
    flex-direction: column;
    flex-wrap: wrap;
    padding: 12px 14px 14px;
    gap: 12px;
  }
  .ch-funnel-top-cols > .ch-funnel-col {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    padding: 14px 14px 16px;
  }
  .ch-funnel-col--stages-split {
    min-width: 0;
  }
  .ch-funnel-coverage-split {
    flex-direction: column;
    align-items: center;
  }
  .ch-funnel-coverage-split__body {
    width: 100%;
  }
  .ch-funnel-stage-split:not(.ch-funnel-stage-split--legend-only) {
    flex-direction: column;
    align-items: center;
  }
  .ch-funnel-stage-split--legend-only {
    align-items: stretch;
  }
}
.ch-funnel-four-cols {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  padding: 14px 18px 16px;
  align-items: stretch;
}
.ch-funnel-four-cols > .ch-funnel-col {
  flex: 1 1 0;
  min-width: min(100%, 220px);
  border: 1px solid rgba(26, 26, 26, 0.06);
  border-radius: 12px;
  padding: 14px 14px 16px;
  background: var(--surface-bright);
  box-sizing: border-box;
}
@media (max-width: 899px) {
  .ch-funnel-four-cols {
    flex-direction: column;
    padding: 12px 14px 14px;
  }
  .ch-funnel-four-cols > .ch-funnel-col {
    min-width: 0;
    flex: 1 1 auto;
    width: 100%;
  }
}
.ch-funnel-col__eyebrow {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
  margin: 0 0 6px;
}
.ch-funnel-col__title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px;
  line-height: 1.35;
}
.ch-funnel-col__desc {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 12px;
  line-height: 1.55;
}
.ch-funnel-mini-bar {
  height: 10px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
  margin-bottom: 8px;
}
.ch-funnel-mini-bar__fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.35s ease;
}
.ch-funnel-mini-bar__fill--cart { background: linear-gradient(90deg, #0a85cf, #2e9ad4); }
.ch-funnel-mini-bar__fill--checkout { background: linear-gradient(90deg, #5cb3a8, #0d9488); }
.ch-funnel-mini-bar__fill--other { background: linear-gradient(90deg, #94a3b8, #64748b); }
.ch-funnel-col__pct {
  font-size: 18px;
  font-weight: 700;
  color: #0a85cf;
  margin: 0 0 10px;
  font-variant-numeric: tabular-nums;
}
.ch-funnel-col__badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(10, 133, 207, 0.08);
  color: #0369a1;
  border: 1px solid rgba(10, 133, 207, 0.15);
}
.ch-funnel-metrics-lead {
  font-size: 13px;
  color: #4b5563;
  margin: 0 0 12px;
  line-height: 1.55;
}
.ch-funnel-coverage-text {
  font-size: 13px;
  color: #374151;
  margin: 0;
  line-height: 1.55;
}
.ch-funnel-coverage-text strong {
  color: #1a1a1a;
  font-weight: 700;
}
.ch-funnel-insight-row {
  padding: 12px 18px 18px;
  border-top: 1px solid rgba(26, 26, 26, 0.06);
  background: linear-gradient(180deg, rgba(251, 250, 247, 0.6) 0%, var(--surface-bright) 100%);
}
.ch-funnel-empty-pad {
  padding: 18px 20px;
}
.ch-funnel-no-tracked-inline {
  margin-top: 12px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.55;
}
.ch-funnel-stack-bar {
  display: flex;
  width: 100%;
  height: 14px;
  border-radius: 8px;
  overflow: hidden;
  background: #e5e7eb;
  margin-top: 4px;
}
.ch-funnel-stack-bar__seg {
  flex-shrink: 0;
  height: 100%;
  min-width: 0;
  transition: width 0.35s ease;
}
.ch-funnel-stack-bar__seg--tracked {
  background: linear-gradient(90deg, rgb(10, 133, 207), rgb(41, 175, 255));
}
.ch-funnel-stack-bar__seg--untracked {
  background: linear-gradient(90deg, rgb(255, 255, 255), rgb(211, 211, 211));
}
.ch-funnel-stack-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.45;
  color: #4b5563;
}
.ch-funnel-stack-meta__item {
  padding-left: 14px;
  position: relative;
}
.ch-funnel-stack-meta__item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.4em;
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
.ch-funnel-stack-meta__item--tracked::before {
  background: linear-gradient(135deg, rgb(10, 133, 207), rgb(41, 175, 255));
}
.ch-funnel-stack-meta__item--untracked::before {
  background: rgb(211, 211, 211);
}
.ch-help-legend__accent {
  height: 4px;
  background: linear-gradient(90deg, #0a85cf 0%, #2e9ad4 45%, #5cb3a8 100%);
}
.ch-help-legend__summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  background: linear-gradient(180deg, rgba(232, 244, 252, 0.55) 0%, var(--surface-bright) 100%);
  border: none;
  border-bottom: 1px solid rgba(26, 26, 26, 0.06);
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  font-family: var(--font);
}
.ch-help-legend__chev {
  margin-left: auto;
  width: 10px;
  height: 10px;
  border-right: 2px solid var(--accent);
  border-bottom: 2px solid var(--accent);
  transform: rotate(45deg);
  flex-shrink: 0;
  transition: transform 0.2s ease;
}
.ch-help-legend__summary[aria-expanded="true"] .ch-help-legend__chev {
  transform: rotate(-135deg);
  margin-top: 4px;
}
.ch-help-legend__sum-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  flex-shrink: 0;
}
.ch-help-legend__sum-hint {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  margin-top: 2px;
}
.ch-help-legend__panel { padding: 0; }
.ch-help-legend__head {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 18px 20px 16px;
  border-bottom: 1px solid rgba(26, 26, 26, 0.06);
  background: linear-gradient(180deg, rgba(232, 244, 252, 0.55) 0%, var(--surface-bright) 100%);
}
.ch-help-legend__head-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
}
.ch-help-legend__title {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 4px;
  letter-spacing: -0.02em;
}
.ch-help-legend__sub {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  max-width: 60ch;
  line-height: 1.5;
}
.ch-help-legend__list { padding: 8px 0; }
.ch-help-legend__row {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px 14px;
  align-items: start;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(26, 26, 26, 0.04);
  transition: background 0.15s ease;
}
.ch-help-legend__row:last-child { border-bottom: none; }
.ch-help-legend__row:hover { background: rgba(10, 133, 207, 0.03); }
.ch-help-legend__rail {
  width: 4px;
  border-radius: 4px;
  min-height: 100%;
  align-self: stretch;
}
.ch-help-legend__rail--neutral { background: linear-gradient(180deg, #9ca3af, #d1d5db); }
.ch-help-legend__rail--warning { background: linear-gradient(180deg, #fb923c, #f97316); }
.ch-help-legend__rail--success { background: linear-gradient(180deg, #4caf50, #2e7d32); }
.ch-help-legend__rail--pa { background: linear-gradient(180deg, #ab47bc, #7b1fa2); }
.ch-help-legend__rail--organic { background: linear-gradient(180deg, #42a5f5, #1565c0); }
.ch-help-legend__rail--completed { background: linear-gradient(180deg, #94a3b8, #64748b); }
.ch-help-legend__rail--savecart { background: linear-gradient(180deg, #0ea5e9, #0284c7); }
.ch-help-legend__body { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.ch-help-legend__text {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.65;
}
.ch-help-legend__tip {
  margin: 0;
  padding: 14px 20px 18px;
  background: linear-gradient(180deg, rgba(251, 250, 247, 0.9) 0%, #fff 100%);
  border-top: 1px solid rgba(26, 26, 26, 0.06);
  font-size: 13px;
  color: #9ca3af;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  line-height: 1.55;
}
.ch-help-legend__tip svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--accent);
  opacity: 0.85;
}
/* Popup on-site — callout esempi pratici (soglie) */
.ch-onsite-example {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: 0 0 12px;
  padding: 12px 14px;
  background: linear-gradient(180deg, rgba(232, 244, 252, 0.4) 0%, #fafbfc 100%);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  border-left: 3px solid var(--accent);
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.ch-onsite-example:last-of-type { margin-bottom: 0; }
.ch-onsite-example__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(10, 133, 207, 0.1);
  color: var(--accent);
}
.ch-onsite-example__title {
  font-weight: 600;
  font-size: 12px;
  color: #374151;
  margin: 0 0 4px;
}
.ch-onsite-example__body {
  margin: 0;
}
/* PDP threshold: toggle espande pannello sotto */
.ch-onsite-pdp-expand--open {
  margin-bottom: 16px;
}
.ch-onsite-pdp-expand--closed .ch-onsite-pdp-expand__toggle {
  margin-bottom: 14px;
}
.ch-onsite-pdp-expand--open .ch-onsite-pdp-expand__toggle {
  margin-bottom: 0;
}
.ch-onsite-pdp-expand__panel {
  overflow: hidden;
  margin-top: 8px;
  padding: 12px 14px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(249, 250, 251, 1) 0%, #fafbfc 100%);
}
@media (prefers-reduced-motion: no-preference) {
  .ch-onsite-pdp-expand__panel {
    animation: ch-onsite-pdp-expand-in 0.18s ease-out;
  }
}
@keyframes ch-onsite-pdp-expand-in {
  from { opacity: 0.75; transform: translateY(-3px); }
  to { opacity: 1; transform: translateY(0); }
}
.ch-help-legend__desktop { display: none; }
@media (min-width: 900px) {
  .ch-help-legend__summary { display: none; }
  .ch-help-legend__head--mobile-only { display: none; }
  .ch-help-legend__list { display: none; }
  .ch-help-legend__desktop {
    display: grid;
    grid-template-columns: minmax(200px, 240px) 1fr;
    gap: 0;
    align-items: stretch;
    border-bottom: 1px solid rgba(26, 26, 26, 0.06);
  }
  .ch-help-legend__desktop-intro {
    padding: 14px 18px;
    background: linear-gradient(180deg, rgba(232, 244, 252, 0.55) 0%, var(--surface-bright) 100%);
    border-right: 1px solid rgba(26, 26, 26, 0.06);
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }
  .ch-help-legend__desktop-intro .ch-help-legend__head-icon { width: 40px; height: 40px; }
  .ch-help-legend__desktop-intro .ch-help-legend__title { font-size: 15px; margin-bottom: 2px; }
  .ch-help-legend__desktop-intro .ch-help-legend__sub { font-size: 12px; max-width: none; line-height: 1.45; }
  .ch-help-legend__grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0;
    padding: 10px 12px 12px;
    align-items: stretch;
  }
  .ch-help-legend__grid--3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .ch-help-legend__grid--4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .ch-help-legend__grid--6 {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
  .ch-help-legend__grid--7 {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
  .ch-help-legend__grid--8 {
    grid-template-columns: repeat(8, minmax(0, 1fr));
  }
  .ch-help-legend__cell {
    padding: 8px 10px;
    border-left: 1px solid rgba(26, 26, 26, 0.05);
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }
  .ch-help-legend__cell:first-child { border-left: none; }
  .ch-help-legend__cell:hover { background: rgba(10, 133, 207, 0.03); border-radius: 8px; }
  .ch-help-legend__cell-rail {
    height: 3px;
    border-radius: 2px;
    width: 100%;
  }
  .ch-help-legend__cell-rail--neutral { background: linear-gradient(90deg, #9ca3af, #d1d5db); }
  .ch-help-legend__cell-rail--warning { background: linear-gradient(90deg, #fb923c, #f97316); }
  .ch-help-legend__cell-rail--success { background: linear-gradient(90deg, #4caf50, #2e7d32); }
  .ch-help-legend__cell-rail--pa { background: linear-gradient(90deg, #ab47bc, #7b1fa2); }
  .ch-help-legend__cell-rail--organic { background: linear-gradient(90deg, #42a5f5, #1565c0); }
  .ch-help-legend__cell-rail--savecart { background: linear-gradient(90deg, #0ea5e9, #0284c7); }
  .ch-help-legend__cell-rail--completed { background: linear-gradient(90deg, #94a3b8, #64748b); }
  .ch-help-legend__grid .ch-status {
    font-size: 11px;
    padding: 3px 8px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ch-help-legend__cell-text {
    font-size: 11px;
    line-height: 1.5;
    color: #6b7280;
    margin: 0;
    overflow: visible;
  }
  .ch-help-legend__tip { padding: 10px 18px 12px; font-size: 12px; }
}

/* Price Alert — blocco import WordPress (sola lettura, accent teal vs help-legend blu) */
.ch-pa-wp-import {
  position: relative;
  border-radius: 12px;
  border: 1px solid rgba(13, 148, 136, 0.2);
  background: linear-gradient(180deg, rgba(240, 253, 250, 0.5) 0%, var(--surface-bright) 55%);
  overflow: hidden;
  margin-bottom: 16px;
}
.ch-pa-wp-import--loading {
  padding: 12px 16px;
  font-size: 14px;
  color: var(--text3);
}
.ch-pa-wp-import__accent {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 4px;
  width: auto;
  background: linear-gradient(90deg, #2dd4bf, #0d9488, #5eead4);
  z-index: 0;
  border-radius: 12px 12px 0 0;
}
.ch-pa-wp-import__inner {
  position: relative;
  z-index: 1;
  padding: 4px 0 0 0;
}
.ch-pa-wp-import__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px 10px 14px;
  background: linear-gradient(180deg, rgba(204, 251, 241, 0.35) 0%, rgba(255, 255, 255, 0) 100%);
  border-bottom: 1px solid rgba(13, 148, 136, 0.12);
}
.ch-pa-wp-import__head-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(13, 148, 136, 0.12);
  color: #0d9488;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ch-pa-wp-import__head-icon svg { display: block; }
.ch-pa-wp-import__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text);
  margin: 0 0 4px;
  letter-spacing: -0.02em;
}
.ch-pa-wp-import__sub {
  margin: 0;
  font-size: 12px;
  color: #5b6b6a;
  line-height: 1.45;
  max-width: 56ch;
}
.ch-pa-wp-import__alert {
  margin: 0 16px 10px 14px;
  padding: 10px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  color: #92400e;
  font-size: 13px;
  line-height: 1.5;
}
.ch-pa-wp-import__split {
  display: flex;
  width: 100%;
  padding: 20px;
  gap: 20px;
  box-sizing: border-box;
}
.ch-pa-wp-import__col {
  display: flex;
  flex: 1;
  min-width: 0;
  position: relative;
  border-top: 1px solid rgba(13, 148, 136, 0.1);
}
.ch-pa-wp-import__col--pa {
  border-right: 1px solid rgba(13, 148, 136, 0.12);
}
.ch-pa-wp-import__col-rail {
  width: 4px;
  flex-shrink: 0;
  background: linear-gradient(180deg, #e2e2e2 10%, #6a1b9a00 100%);
  align-self: stretch;
}
.ch-pa-wp-import__col-rail--poe {
  background: linear-gradient(180deg, #beecdd 5%, #c2410c00 80%);
}
.ch-pa-wp-import__col-body {
  flex: 1;
  min-width: 0;
  padding: 12px 14px 16px 12px;
}
.ch-pa-wp-import__col-title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.35;
  color: #0f766e;
  margin: 0 0 12px;
}
.ch-pa-wp-import__col--poe .ch-pa-wp-import__col-title {
  color: #c2410c;
}
.ch-pa-wp-import__row {
  margin-bottom: 10px;
}
.ch-pa-wp-import__row:last-child { margin-bottom: 0; }
.ch-pa-wp-import__row-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  margin-bottom: 4px;
}
.ch-pa-wp-import__row-value {
  font-size: 14px;
  color: var(--text);
  line-height: 1.5;
  word-wrap: break-word;
}
.ch-pa-wp-import__row-value--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  word-break: break-all;
}
.ch-pa-wp-import__scope-note {
  margin: 0 0 6px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text2);
}
.ch-pa-wp-import__scope-note--muted {
  color: #9ca3af;
}
.ch-pa-wp-import__ids { font-size: 12px; color: var(--text2); line-height: 1.4; margin-top: 4px; }
.ch-pa-wp-import__id-label { font-size: 11px; font-weight: 600; color: #9ca3af; text-transform: uppercase; }
.ch-pa-wp-import__poe-list { margin: 0; padding: 0 0 0 16px; color: var(--text2); font-size: 13px; line-height: 1.5; }
.ch-pa-wp-import__poe-list li { margin-bottom: 10px; }
.ch-pa-wp-import__poe-fence-name { font-weight: 600; color: var(--text); }
.ch-pa-wp-import__poe-offer-inline { font-weight: 500; color: #64748b; }
.ch-pa-wp-import__poe-fence-ids { font-size: 11px; color: #94a3b8; font-family: ui-monospace, monospace; margin-top: 4px; word-break: break-all; }
.ch-pa-wp-import__poe-offer-line { margin: 6px 0 0; font-size: 15px; }
.ch-pa-wp-import__muted { color: #9ca3af; font-size: 12px; font-weight: 500; }
.ch-pa-wp-import__hint { margin: 0 0 8px; font-size: 12px; color: #94a3b8; line-height: 1.4; }
.ch-pa-wp-import__foot {
  padding: 10px 18px 14px 18px;
  font-size: 12px;
  color: #64748b;
  border-top: 1px solid rgba(13, 148, 136, 0.1);
  background: rgba(255, 255, 255, 0.5);
}
@media (max-width: 899px) {
  .ch-pa-wp-import__col--pa { border-right: none; }
}

/* Recovery — Protezione margine: rail, scenari, details nativo, callout (vedi RecoveryMarginProtectionCard.js) */
.ch-margin-card.ch-card h2.ch-margin-card__title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 6px;
}
.ch-margin-card__title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1a1a1a;
}
.ch-margin-card__title svg { flex-shrink: 0; color: var(--accent); }
.ch-margin-card.ch-card p.ch-margin-card__intro {
  margin: 0 0 22px;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.55;
}
.ch-margin-card__intro strong { color: #374151; }
.ch-margin-row {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 0;
  border-top: 1px solid rgba(26, 26, 26, 0.08);
}
.ch-margin-row:first-of-type {
  border-top: none;
  padding-top: 0;
}
.ch-margin-row__rail {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
}
.ch-margin-row__rail svg { display: block; }
.ch-margin-row__body { flex: 1; min-width: 0; }
.ch-margin-row__label-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.ch-margin-row__title-label {
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
  cursor: pointer;
  flex: 1;
  min-width: 160px;
  line-height: 1.4;
  padding-top: 1px;
}
.ch-margin-row__hint {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
}
.ch-margin-row__hint code {
  font-size: 12px;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
}
.ch-margin-scenario {
  margin-top: 12px;
  padding: 12px 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}
.ch-margin-scenario strong { color: #1a1a1a; }
.ch-margin-scenario__tag {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--accent);
  margin-bottom: 6px;
}
.ch-margin-scenario p { margin: 0; }
/* Guida budget / forte promo — collassabile (details), mock: /root/docs/carthub-recovery-margin-guide-layout-mockup.html */
.ch-margin-guide-details {
  margin: 0 0 18px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: var(--surface-bright);
  overflow: hidden;
}
.ch-margin-guide-details__summary {
  list-style: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  font-family: var(--font);
  background: #f3f4f6;
  border: none;
  width: 100%;
  text-align: left;
  box-sizing: border-box;
}
.ch-margin-guide-details__summary::-webkit-details-marker {
  display: none;
}
.ch-margin-guide-details__summary::marker {
  content: "";
}
.ch-margin-guide-details__summary-text {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.4;
}
.ch-margin-guide-details__hint {
  font-weight: 400;
  color: #6b7280;
}
.ch-margin-guide-details__chev {
  flex-shrink: 0;
  color: #6b7280;
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
}
.ch-margin-guide-details[open] .ch-margin-guide-details__chev {
  transform: rotate(90deg);
}
.ch-margin-guide-details__panel {
  padding: 14px 14px 16px;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
}
.ch-margin-guide__lead {
  margin: 0 0 14px;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.5;
  text-align: left;
}
.ch-margin-guide__step {
  margin-bottom: 12px;
}
.ch-margin-guide__step:last-of-type {
  margin-bottom: 0;
}
.ch-margin-guide__step-main {
  width: 100%;
  box-sizing: border-box;
  background: var(--surface-bright);
  border: 1px solid rgba(26, 26, 26, 0.08);
  border-radius: 10px;
  padding: 14px 16px 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.ch-margin-guide__step-title {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.02em;
  line-height: 1.3;
}
.ch-margin-guide__step-body {
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
}
.ch-margin-guide__step-body p {
  margin: 0 0 10px;
}
.ch-margin-guide__step-body p:last-child {
  margin-bottom: 0;
}
.ch-margin-guide__list {
  margin: 8px 0 0;
  padding-left: 1.2em;
}
.ch-margin-guide__list li {
  margin-bottom: 8px;
}
.ch-margin-guide__list li:last-child {
  margin-bottom: 0;
}
.ch-margin-guide__formula {
  margin: 12px 0 0;
  padding: 12px 14px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  font-size: 13px;
  color: #0c4a6e;
  line-height: 1.55;
}
.ch-margin-guide__formula strong {
  color: #0369a1;
  font-weight: 600;
}
.ch-margin-guide__compare {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 4px;
}
.ch-margin-guide__pill {
  background: var(--surface-bright);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  min-width: 0;
}
.ch-margin-guide__pill-title {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}
.ch-margin-guide__pill-dl {
  margin: 0;
  font-size: 13px;
  color: #374151;
}
.ch-margin-guide__pill-dl dt {
  font-weight: 600;
  color: #1a1a1a;
  margin-top: 10px;
}
.ch-margin-guide__pill-dl dt:first-child {
  margin-top: 0;
}
.ch-margin-guide__pill-dl dd {
  margin: 4px 0 0;
  padding: 0;
  line-height: 1.5;
}
.ch-margin-guide__foot {
  margin-top: 30px !important;
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 0;
  padding: 12px 14px;
  background: var(--surface-bright);
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
  text-align: center;
  line-height: 1.5;
}
@media (max-width: 560px) {
  .ch-margin-guide__compare {
    grid-template-columns: 1fr;
  }
}
.ch-margin-threshold { margin-top: 10px; }
.ch-margin-threshold__label {
  display: block;
  font-weight: 500;
  font-size: 13px;
  color: #374151;
  margin-bottom: 6px;
}
.ch-margin-threshold__row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.ch-margin-threshold__track {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  max-width: 220px;
  min-width: 80px;
  background: linear-gradient(90deg, #e5e7eb, var(--accent));
}
.ch-margin-threshold__hint { margin-top: 8px; }
.ch-margin-details {
  margin-top: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
.ch-margin-details__summary {
  padding: 12px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: #fafafa;
  list-style: none;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1a1a1a;
}
.ch-margin-details__summary::-webkit-details-marker { display: none; }
.ch-margin-details__summary::marker { content: ''; }
.ch-margin-details[open] .ch-margin-details__summary {
  border-bottom: 1px solid #e5e7eb;
}
.ch-margin-details__summary svg { flex-shrink: 0; color: var(--accent); }
.ch-margin-details__body {
  padding: 14px 16px 16px;
  font-size: 13px;
  color: #374151;
  line-height: 1.55;
}
.ch-margin-details__body p { margin: 0; }
.ch-margin-callout {
  margin-top: 22px;
  padding: 14px 16px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.ch-margin-callout svg { flex-shrink: 0; margin-top: 2px; color: #d97706; }
.ch-margin-callout strong { display: block; margin-bottom: 4px; color: #78350f; }
.ch-margin-callout code {
  font-size: 12px;
  background: #fef3c7;
  padding: 1px 5px;
  border-radius: 4px;
}
.ch-margin-card.ch-card p.ch-margin-budget {
  margin: 18px 0 12px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.55;
}
.ch-margin-budget strong { color: #374151; }
@media (max-width: 480px) {
  .ch-margin-row {
    flex-direction: column;
    gap: 10px;
  }
  .ch-margin-row__rail {
    align-self: flex-start;
  }
}

/* Timing recovery automatico — stesso linguaggio visivo di .ch-help-legend (fascia + card + tip) */
.ch-timing-matrix-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 4px;
}
.ch-timing-matrix {
  background: var(--surface-bright);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(26, 26, 26, 0.06);
  box-shadow: 0 6px 20px rgba(26, 26, 26, 0.05);
  min-width: 280px;
}
.ch-timing-matrix__accent {
  height: 3px;
  background: linear-gradient(90deg, #0a85cf 0%, #2e9ad4 45%, #5cb3a8 100%);
}
.ch-timing-matrix table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.ch-timing-matrix thead th {
  text-align: left;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
  background: linear-gradient(180deg, rgba(232, 244, 252, 0.7) 0%, var(--surface-bright) 100%);
  border-bottom: 1px solid rgba(26, 26, 26, 0.06);
  white-space: nowrap;
}
.ch-timing-matrix thead th:first-child {
  min-width: 120px;
}
.ch-timing-matrix tbody td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(26, 26, 26, 0.04);
  vertical-align: middle;
}
.ch-timing-matrix tbody tr:last-child td {
  border-bottom: none;
}
.ch-timing-matrix tbody tr:hover td {
  background: rgba(10, 133, 207, 0.04);
}
.ch-timing-matrix__fascia {
  font-weight: 600;
  color: #374151;
  font-size: 13px;
}
.ch-timing-matrix__step {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--accent);
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
  white-space: nowrap;
}
.ch-timing-matrix__foot {
  margin: 0;
  padding: 10px 14px 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #9ca3af;
  border-top: 1px solid rgba(26, 26, 26, 0.06);
  background: linear-gradient(180deg, rgba(251, 250, 247, 0.9) 0%, #fff 100%);
}

/* ch-status badge */
.ch-status { font-size: 13px; font-weight: 500; padding: 3px 10px; border-radius: 20px; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.ch-status svg { width: 12px; height: 12px; flex-shrink: 0; }
.ch-status.success { color: #2e7d32; background: #f0faf0; border: 1px solid #c8e6c9; }
.ch-status.error   { color: #c62828; background: #fff5f5; border: 1px solid #ffcdd2; }
.ch-status.warning { color: #e65100; background: #fff8f0; border: 1px solid #ffe0b2; }
.ch-status.neutral { color: #6b7280; background: #f5f5f5; border: 1px solid #e5e5e5; }
.ch-status.info    { color: var(--accent); background: var(--accent-bg); border: 1px solid var(--accent-border); }
.ch-status.organic { color: #1565c0; background: #e3f2fd; border: 1px solid #90caf9; }
.ch-status.assisted-pa { color: #6a1b9a; background: #f3e5f5; border: 1px solid #ce93d8; }
.ch-status.pa-active    { color: #0277bd; background: #e1f5fe; border: 1px solid #81d4fa; }
.ch-status.pa-sent      { color: #e65100; background: #fff3e0; border: 1px solid #ffcc80; }
.ch-status.pa-converted { color: #2e7d32; background: #e8f5e9; border: 1px solid #a5d6a7; }
.ch-status.pa-expired   { color: #6b7280; background: #f5f5f5; border: 1px solid #e5e5e5; }
.status-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }

/* ch-status-table */
.ch-status-table { width: 100%; border-collapse: collapse; font-size: 15px; }
.ch-status-table tr { border-bottom: 1px solid #f5f5f5; }
.ch-status-table tr:last-child { border-bottom: none; }
.ch-status-table th { width: 180px; padding: 12px 0; text-align: left; font-weight: 500; color: #6b7280; }
.ch-status-table td { padding: 12px 0; color: #1a1a1a; }

/* ch-divider */
.ch-divider { border: none; border-top: 1px solid #e5e5e5; margin: 20px 0; }

/* ch-actions */
.ch-actions { display: flex; align-items: center; gap: 12px; padding-top: 16px; border-top: 1px solid #e5e5e5; margin-top: 4px; }

/* ch-search */
.ch-search { padding: 7px 12px; font-size: 15px; font-family: var(--font); border: 1px solid #e5e5e5; border-radius: 6px; outline: none; color: #1a1a1a; transition: border-color .15s; background: #fff; }
.ch-search:focus { border-color: var(--accent); }

/* ch-brands-header / ch-brand-row (da admin.css) */
.ch-brands-header { display: grid; grid-template-columns: 28px 1fr 160px 100px 70px; gap: 10px; padding: 8px 14px; background: #f9fafb; border-bottom: 1px solid #e5e5e5; font-size: 13px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: .05em; }
.ch-brand-row { display: grid; grid-template-columns: 28px 1fr 160px 100px 70px; gap: 10px; align-items: center; padding: 10px 14px; background: #ffffff; border-bottom: 1px solid #f5f5f5; transition: background .1s; font-size: 15px; }
.ch-brand-row:last-child { border-bottom: none; }
.ch-brand-row:hover { background: #fafafa; }
.brand-av { width: 24px; height: 24px; border-radius: 5px; background: var(--accent-bg); border: 1px solid var(--accent-border); display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600; color: var(--accent); flex-shrink: 0; }

/* toggle */
.ch-toggle { position: relative; width: 36px; height: 20px; cursor: pointer; display: inline-block; flex-shrink: 0; }
.ch-toggle input { opacity: 0; width: 0; height: 0; }
.ch-toggle-track { position: absolute; inset: 0; background: #e5e5e5; border-radius: 20px; transition: all .2s; border: 1px solid #d1d1d1; }
.ch-toggle-track::before { content: ''; position: absolute; width: 14px; height: 14px; left: 2px; top: 2px; background: #fff; border-radius: 50%; transition: all .2s; box-shadow: var(--shadow-module); }
.ch-toggle input:checked + .ch-toggle-track { background: var(--accent); border-color: var(--accent); }
.ch-toggle input:checked + .ch-toggle-track::before { transform: translateX(16px); }

/* On-site popup — mostra su mobile: icona smartphone + toggle + etichetta */
.ch-onsite-mobile-toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.ch-onsite-mobile-toggle-row__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--accent);
}
.ch-onsite-mobile-toggle-row__icon svg { display: block; }
.ch-onsite-mobile-toggle-row__label {
  font-size: 14px;
  color: #374151;
  line-height: 1.45;
  cursor: pointer;
  flex: 1;
  min-width: 200px;
}

.ch-onsite-popup-settings code {
  font-family: source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
  font-weight: 400;
  font-size: 16px !important;
  color: #0a85cf;
}

/* copy-field */
.copy-field { display: flex; gap: 8px; align-items: center; }
.copy-value { background: #f5f5f5; border: 1px solid #e5e5e5; border-radius: 6px; padding: 8px 12px; font-size: 13px; color: #6b7280; font-family: monospace; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 320px; }
@media (max-width: 640px) { .copy-value { max-width: 100%; } }

/* Brands — barra ricerca sopra lista */
.brands-list-toolbar { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.brands-list-toolbar .ch-search { flex: 1 1 180px; min-width: 0; max-width: 100%; width: auto; }

/* table */
.table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; max-width: 100%; }
/* Tabella con molte colonne: larghezza minima → scroll orizzontale invece di colonne schiacciate */
.table-wrap.table-min-wide table { min-width: 880px; }
@media (max-width: 640px) {
  .table-wrap.table-min-wide thead th,
  .table-wrap.table-min-wide tbody td { padding: 8px 10px; font-size: 14px; }
}
.ch-table {
  background: var(--surface-panel);
  max-width: 1300px;
  margin: 0 auto;
  padding: 16px;
  border-radius: var(--radius-module);
  box-shadow: var(--shadow-module);
  color: #333 !important;
}
table { width: 100%; border-collapse: collapse; font-size: 15px; }
thead th { padding: 8px 14px; text-align: left; font-size: 13px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #e5e5e5; background: #f9fafb; white-space: nowrap; }
tbody tr { border-bottom: 1px solid #f5f5f5; transition: background .1s; }
tbody tr:last-child { border-bottom: none; }
tbody tr:hover { background: #fafafa; }
tbody td { padding: 12px 14px; vertical-align: middle; color: #1a1a1a; }
.td-muted { color: #6b7280; }
.td-sm { font-size: 13px; color: #9ca3af; margin-top: 2px; }

/* stat card — KPI principali (Stats, Price Alerts, …): 4 colonne desktop */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}
.stat-card { background: var(--surface-panel); border: none; border-radius: var(--radius-module); padding: 20px; box-shadow: var(--shadow-module); }
.stat-label { font-size: 13px; font-weight: 500; color: #6b7280; margin-bottom: 8px; }
.stat-val { font-size: 26px; font-weight: 600; color: #1a1a1a; line-height: 1; margin-bottom: 6px; }
.stat-val.accent { color: var(--accent); }
.stat-sub { font-size: 13px; color: #9ca3af; }
.stat-sub.up { color: #2e7d32; }

/* Statistiche popup — KPI carrello + tre conteggi eventi: quattro colonne uguali desktop */
.ch-popup-metrics-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  align-items: stretch;
}
.ch-popup-metrics-row > .stat-card { min-width: 0; height: 100%; box-sizing: border-box; margin: 0; }
.ch-popup-metric-cell {
  min-width: 0;
  padding: 12px 14px;
  background: var(--surface-panel);
  border-radius: var(--radius-module);
  border: 1px solid rgba(26, 26, 26, 0.08);
  box-shadow: var(--shadow-module);
  box-sizing: border-box;
}
.ch-popup-metric-val { font-size: 22px; font-weight: 700; color: #1a1a1a; line-height: 1.2; }
.ch-popup-metric-meta { font-size: 12px; color: #9ca3af; margin-top: 4px; line-height: 1.35; }

/* progress */
.progress-wrap { height: 5px; background: #f5f5f5; border-radius: 3px; overflow: hidden; }
.progress-bar { height: 100%; background: var(--accent); border-radius: 3px; }

/* mini chart */
.mini-chart { display: flex; align-items: flex-end; gap: 4px; }
.mini-bar { flex: 1; background: var(--accent-bg); border: 1px solid var(--accent-border); border-bottom: none; border-radius: 3px 3px 0 0; min-height: 4px; }
.mini-bar.now { background: var(--accent); border-color: var(--accent-hover); }

/* kv-row */
.kv-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid #f5f5f5; font-size: 15px; }
.kv-row:last-child { border-bottom: none; }
.kv-label { color: #6b7280; }
.kv-val { font-weight: 500; color: #1a1a1a; }

/* grid — colonne uguali (minmax 0 evita overflow che ruba spazio alla colonna affiancata) */
.grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--gap-module); }
.grid-3 { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--gap-module); }

/* Stats — Top prodotti recuperati / abbandonati: 50|50 desktop, colonna singola sotto ~1024px; tabelle fluide nella card */
.stats-top-products { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--gap-module); align-items: start; }
.stats-top-products .ch-card { min-width: 0; }
.stats-top-products .table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.stats-top-products table { width: 100%; table-layout: fixed; }
.stats-top-products thead th:first-child,
.stats-top-products tbody td:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 38%;
}
@media (max-width: 1024px) {
  .stats-top-products { grid-template-columns: 1fr; }
}
@media (max-width: 768px) { .grid-3 { grid-template-columns: 1fr; } }

/* Subscription — piani a colonna singola su tablet/phone */
.subscription-plans-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
@media (max-width: 900px) {
  .subscription-plans-grid { grid-template-columns: 1fr; }
}
.subscription-plans-grid li svg { width: 14px; height: 14px; flex-shrink: 0; }
.subscription-plans-grid .ch-btn svg { width: 14px; height: 14px; flex-shrink: 0; }

/* PriceAlerts & sezioni simili — card larghezza piena, coerente con token */
.section-card {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: var(--surface-panel);
  border: none;
  border-radius: var(--radius-module);
  padding: 16px 18px;
  margin-bottom: var(--gap-module);
  box-shadow: var(--shadow-module);
}
.section-card .ch-table { max-width: none; margin: 0; }

/* Carts — toolbar filtri responsive */
.carts-toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
}
.carts-toolbar .ch-search { flex: 1 1 200px; min-width: 0; max-width: 100%; width: auto; }
.carts-toolbar-filters { display: flex; gap: 6px; flex-wrap: wrap; margin-left: auto; align-items: center; }
@media (max-width: 640px) {
  .carts-toolbar-filters { margin-left: 0; width: 100%; }
}

/* Brands — griglia brand: scroll orizzontale su narrow */
.ch-brands-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  max-width: 100%;
}
.ch-brands-table-wrap .ch-brands-header,
.ch-brands-table-wrap .ch-brand-row { min-width: 520px; }

/* Subscription header card */
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 12px;
}
.card-head h2 { margin: 0; }

/* topbar pill */
.pill { font-size: 13px; color: #6b7280; background: var(--surface-bright); border: none; border-radius: 20px; padding: 4px 11px; white-space: nowrap; box-shadow: var(--shadow-module); display: inline-flex; align-items: center; gap: 6px; }
.pill svg { width: 14px; height: 14px; flex-shrink: 0; }
.pill.green { color: #2e7d32; background: #f0faf0; border-color: #c8e6c9; }

/* empty */
.empty { text-align: center; padding: 48px 20px; color: #9ca3af; font-size: 15px; }
.empty-icon { font-size: 28px; opacity: .5; margin-bottom: 8px; }
.empty-icon-svg {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 8px;
  color: var(--text2);
  opacity: 0.45;
}
.empty-icon-svg svg { width: 28px; height: 28px; flex-shrink: 0; }
.ch-inline-icon { display: inline-flex; align-items: center; justify-content: center; vertical-align: middle; margin-right: 4px; color: currentColor; }
.ch-inline-icon svg { width: 1em; height: 1em; flex-shrink: 0; }
.ch-app-banner__icon { display: inline-flex; align-items: center; justify-content: center; color: var(--text2); }
.ch-app-banner__icon svg { width: 18px; height: 18px; }
.dns-step-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 50%;
  border: 1.5px solid var(--text2);
  font-size: 11px;
  font-weight: 600;
  color: var(--text2);
  flex-shrink: 0;
}
.empty-title { font-size: 15px; font-weight: 500; color: #6b7280; margin-bottom: 4px; }
.ch-stat-activity-ic { display: flex; align-items: center; justify-content: center; color: var(--text2); }
.ch-stat-activity-ic svg { width: 16px; height: 16px; flex-shrink: 0; }
.ch-recovery-email-ic { display: flex; align-items: center; justify-content: center; }
.ch-recovery-email-ic svg { width: 20px; height: 20px; flex-shrink: 0; }

/* login — sfondo SVG invariato; card allineata ai token dashboard */
.login-wrap {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  background-color: var(--canvas);
  background-image: url('https://carthub.cloud/wp-content/uploads/2026/03/svgviewer-output1.svg');
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
}
.login-card {
  width: 100%;
  max-width: 400px;
  background: var(--surface-panel);
  border: none;
  border-radius: var(--radius-module);
  padding: 40px 36px;
  box-shadow: var(--shadow-module);
}
.login-logo { font-size: 26px; font-weight: 700; letter-spacing: -0.03em; margin-bottom: 6px; color: #1a1a1a; line-height: 1.15; }
.login-logo span { color: var(--accent); }
.login-sub { font-size: 15px; color: #6b7280; margin-bottom: 28px; }
.login-lead { font-size: 14px; color: #4b5563; line-height: 1.55; margin: 0 0 20px; }
.login-label { display: block; font-size: 14px; font-weight: 500; color: #1a1a1a; margin-bottom: 6px; }
.login-input { width: 100%; box-sizing: border-box; padding: 10px 12px; font-size: 15px; font-family: var(--font); border: 1px solid rgba(26, 26, 26, 0.08); border-radius: 12px; color: #1a1a1a; background: var(--surface-bright); outline: none; transition: border-color .15s, box-shadow .15s; margin-bottom: 14px; }
.login-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-glow); }
.login-link-btn {
  display: block;
  width: 100%;
  margin: 4px 0 10px;
  padding: 0;
  border: none;
  background: none;
  font-family: var(--font);
  font-size: 14px;
  font-weight: 500;
  color: var(--link);
  cursor: pointer;
  text-align: right;
  text-decoration: none;
}
.login-link-btn:hover { text-decoration: underline; color: var(--accent-hover); }
.login-link-btn--after { text-align: center; margin-top: 16px; }
.login-btn { width: 100%; background: #1a1a1a; color: #fff; border: 1px solid #1a1a1a; border-radius: 8px; padding: 11px; font-family: var(--font); font-size: 15px; font-weight: 600; cursor: pointer; transition: background .15s, transform .1s; margin-top: 8px; }
.login-btn:hover { background: #333; }
.login-btn:active { transform: scale(0.99); }
.login-btn:disabled { opacity: .5; cursor: not-allowed; }
.login-err { background: #fff5f5; border: 1px solid #ffcdd2; border-radius: 8px; color: #c62828; font-size: 13px; padding: 10px 12px; margin-top: 14px; line-height: 1.4; }
.login-url-hint-bubble {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--text);
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
}
.login-url-hint-bubble code {
  font-size: 12px;
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(255,255,255,0.85);
  border: 1px solid var(--accent-border);
  word-break: break-all;
}
.login-ok { background: #f0faf6; border: 1px solid #b2dfdb; border-radius: 8px; color: #1b5e20; font-size: 13px; padding: 10px 12px; margin-top: 14px; line-height: 1.4; }
.login-footer { margin-top: 22px; padding-top: 20px; border-top: 1px solid rgba(26, 26, 26, 0.08); text-align: center; }
.login-footer-muted { display: block; font-size: 13px; color: #9ca3af; margin-bottom: 8px; }
.login-footer-link { font-size: 14px; font-weight: 600; color: var(--link); text-decoration: none; }
.login-footer-link:hover { text-decoration: underline; color: var(--accent-hover); }
.login-lang-footer { margin-top: 20px; padding-top: 16px; border-top: 1px solid rgba(26, 26, 26, 0.08); }
.login-checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -4px 0 14px;
  font-size: 13px;
  color: var(--text2);
  user-select: none;
}
.login-checkbox-row input { width: auto; margin: 0; cursor: pointer; accent-color: var(--accent); }
.login-checkbox-row label { cursor: pointer; font-weight: 400; color: var(--text2); }
.language-selector--login { margin: 0; padding: 0; }
.language-selector__login-row { display: flex; align-items: center; gap: 10px; }
.language-selector__globe { flex-shrink: 0; color: #6b7280; }
.language-selector__select--login { cursor: pointer; }

@media (max-width: 1200px) { .stats-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 900px) {
  .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ch-popup-metrics-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 480px) {
  .ch-popup-metrics-row { grid-template-columns: 1fr; }
}
@media (max-width: 768px)  { .grid-2 { grid-template-columns: 1fr; } }

/* ══ PRO TIER ══ */
.tier-selector { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
.tier-option { flex: 1 1 200px; border: 2px solid #e5e5e5; border-radius: var(--radius-module); padding: 16px; cursor: pointer; transition: all .15s; position: relative; }
.tier-option:hover { border-color: #d1d1d1; }
.tier-option.active { border-color: var(--accent); background: var(--accent-bg); }
.tier-option .tier-badge { font-size: 13px; font-weight: 600; background: var(--accent); color: white; padding: 2px 8px; border-radius: 20px; position: absolute; top: 12px; right: 12px; }
.tier-option h3 { font-size: 15px; font-weight: 600; margin: 0 0 4px; color: #1a1a1a; }
.tier-option p { font-size: 13px; color: #6b7280; margin: 0; line-height: 1.5; }
.tier-radio { width: 16px; height: 16px; border-radius: 50%; border: 2px solid #e5e5e5; display: inline-block; margin-bottom: 8px; position: relative; }
.tier-option.active .tier-radio { border-color: var(--accent); }
.tier-option.active .tier-radio::after { content: ''; width: 8px; height: 8px; border-radius: 50%; background: var(--accent); position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); }
.dns-record { background: #f9fafb; border: 1px solid #e5e5e5; border-radius: 6px; padding: 12px 14px; margin-bottom: 8px; font-size: 13px; }
.dns-record .dns-label { font-weight: 600; color: #6b7280; font-size: 14px; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 4px; }
.dns-record .dns-val { font-family: monospace; color: #1a1a1a; word-break: break-all; }
.dns-guide { border: 1px solid #e5e5e5; border-radius: 8px; margin-bottom: 20px; overflow: hidden; }
.dns-guide-header { padding: 14px 16px; background: #f9fafb; cursor: pointer; display: flex; align-items: center; gap: 10px; font-weight: 600; font-size: 15px; user-select: none; }
.dns-guide-header svg { width: 18px; height: 18px; flex-shrink: 0; }
.dns-guide-header:hover { background: #f3f4f6; }
.dns-guide-body { padding: 16px; border-top: 1px solid #e5e5e5; }
.dns-case { border-radius: 6px; padding: 14px 16px; margin-bottom: 10px; }
.dns-case:last-child { margin-bottom: 0; }
.dns-case-title { font-weight: 600; font-size: 15px; margin-bottom: 6px; display: flex; align-items: center; gap: 8px; }
.dns-case p { font-size: 14px; color: #374151; margin: 0 0 6px; line-height: 1.6; }
.dns-case code { font-family: monospace; font-size: 13px; background: #f3f4f6; padding: 2px 6px; border-radius: 4px; }
.dns-status-row { display: flex; align-items: center; gap: 8px; font-size: 13px; margin-top: 4px; }

/* ══ EMAIL BRANDING ══ */
.branding-wrap { display: flex; gap: 40px; flex-wrap: wrap; align-items: flex-start; }
.branding-form { flex: 1 1 300px; min-width: 260px; }
.branding-preview { flex: 1 1 340px; min-width: 300px; }
.color-field { margin-bottom: 20px; }
.color-field label { display: block; font-size: 15px; font-weight: 500; color: #1a1a1a; margin-bottom: 4px; }
.color-field .hint { font-size: 13px; color: #6b7280; margin-bottom: 8px; }
.color-row { display: flex; align-items: center; gap: 10px; }
.color-row input[type=color] { width: 42px; height: 42px; border: 1px solid #e5e5e5; border-radius: 8px; cursor: pointer; padding: 2px; background: none; }
.color-hex { border: 1px solid #e5e5e5; border-radius: 6px; padding: 8px 10px; font-size: 15px; font-family: monospace; color: #1a1a1a; outline: none; width: 100px; }
.color-hex:focus { border-color: var(--accent); }
.color-swatch { width: 42px; height: 42px; border-radius: 8px; border: 1px solid #e5e5e5; flex-shrink: 0; }
.color-reset { font-size: 13px; color: #9ca3af; background: none; border: none; cursor: pointer; padding: 0; text-decoration: underline; }
.color-reset:hover { color: #6b7280; }
.ep-wrap { border: 1px solid #e5e5e5; border-radius: 8px; overflow: hidden; font-family: Arial, sans-serif; font-size: 15px; box-shadow: var(--shadow-module); padding: 3%; }
.ep-header { padding: 18px 20px; text-align: center; }
.ep-body { background: #fff; padding: 18px 20px 10px; }
.ep-footer { background: #f9f9f9; padding: 10px 20px; text-align: center; border-top: 1px solid #e5e5e5; font-size: 13px; color: #9ca3af; }
.ep-product { background: #f9f9f9; border-radius: 6px; padding: 12px 14px; margin-bottom: 12px; }
.ep-price-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; font-size: 13px; color: #6b7280; }
.ep-cta { text-align: center; padding-bottom: 6px; }
`;

// ─── Icone SVG per sidebar e UI (elementi React già creati: usare {Ic.nome}, mai <Ic.nome />) ───
export const Ic = {
  menu: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>,
  cart: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>,
  chart: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  brand: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  bell: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  barChart: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
  palette: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/><circle cx="6.5" cy="11.5" r=".5" fill="currentColor" stroke="none"/><circle cx="9.5" cy="7.5" r=".5" fill="currentColor" stroke="none"/><circle cx="14.5" cy="7.5" r=".5" fill="currentColor" stroke="none"/><circle cx="17.5" cy="11.5" r=".5" fill="currentColor" stroke="none"/></svg>,
  wallet: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12h.01"/></svg>,
  settings: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  logout: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  back: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>,
  copy: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  check: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  loader: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>,
  package: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  trophy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>,
  clipboardList: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  building: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12h12"/><path d="M6 16h12"/><path d="M10 22v-4h4v4"/><path d="M10 8h.01"/><path d="M14 8h.01"/></svg>,
  bellOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.7 3A6 6 0 0 1 18 8a21.3 21.3 0 0 0 .64 3.88"/><path d="M9.64 18.36A9 9 0 0 0 21 12"/><path d="M12 22a2 2 0 0 0 2-2H10a2 2 0 0 0 2 2Z"/><line x1="2" y1="2" x2="22" y2="22"/></svg>,
  pause: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>,
  play: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  star: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  book: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  wrench: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  lightbulb: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.3A7 7 0 0 0 12 2z"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  chevronDown: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>,
  chevronRight: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>,
  alert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  tag: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  popup: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><rect x="7" y="7" width="10" height="10" rx="1"/></svg>,
  info: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  ),
};

// ─── Dati mock per demo/fallback ───
export const MOCK = {
  stats: { total: 847, recovered: 231, rate: 27.3, revenue: 18420, emails: 712, cost: 0.89 },
  carts: [
    { id:"1", customer_name:"Mario Rossi",    customer_email:"mario.rossi@gmail.com",       cart_value:349.90, recovered:false, abandoned_at:new Date(Date.now()-7200000).toISOString(),   language:"it", cart_data:{items:[{name:"Sony WH-1000XM5",price:349.90,quantity:1}]} },
    { id:"2", customer_name:"Giulia Bianchi", customer_email:"giulia.bianchi@icloud.com",   cart_value:89.90,  recovered:true,  abandoned_at:new Date(Date.now()-86400000).toISOString(),  language:"it", cart_data:{items:[{name:"Nike Air Max 90",price:89.90,quantity:1}]} },
    { id:"3", customer_name:"Luca Moretti",   customer_email:"luca.m@outlook.com",          cart_value:129.00, recovered:false, abandoned_at:new Date(Date.now()-28800000).toISOString(), language:"it", cart_data:{items:[{name:"Adidas Ultraboost",price:129.00,quantity:1}]} },
    { id:"4", customer_name:"Sara Ferrari",   customer_email:"sara.f@gmail.com",            cart_value:55.00,  recovered:false, abandoned_at:new Date(Date.now()-3600000).toISOString(),   language:"it", cart_data:{items:[{name:"L'Oreal Serum",price:29.90,quantity:1},{name:"Nivea Cream",price:12.90,quantity:2}]} },
    { id:"5", customer_name:"John Doe",       customer_email:"john.d@gmail.com",            cart_value:199.00, recovered:true,  abandoned_at:new Date(Date.now()-172800000).toISOString(), language:"en", cart_data:{items:[{name:"Apple AirPods Pro",price:199.00,quantity:1}]} },
  ],
  brands: [
    { id:"1", brand_name:"Sony",    official_website:"https://www.sony.com",   times_enriched:12, active:true,  category_slug:"elettronica" },
    { id:"2", brand_name:"Nike",    official_website:"https://www.nike.com",   times_enriched:8,  active:true,  category_slug:"sportswear" },
    { id:"3", brand_name:"Apple",   official_website:"https://www.apple.com",  times_enriched:5,  active:true,  category_slug:"elettronica" },
    { id:"4", brand_name:"Adidas",  official_website:"https://www.adidas.com", times_enriched:3,  active:true,  category_slug:"sportswear" },
    { id:"5", brand_name:"L'Oreal", official_website:"https://www.loreal.com", times_enriched:2,  active:false, category_slug:"beauty" },
  ],
  rules: [
    { id:"1", label:"Cart €0–50",    max_discount_percent:15, enabled:true },
    { id:"2", label:"Cart €50–100",  max_discount_percent:12, enabled:true },
    { id:"3", label:"Cart €100–200", max_discount_percent:8,  enabled:true },
    { id:"4", label:"Cart €200+",    max_discount_percent:5,  enabled:true },
  ],
};

// ─── Helper functions (EUR / tempo relativo seguono lingua dashboard via i18n) ───
export const eur = (n) => formatEur(i18n.language, n);
export const ago = (iso) => formatAgo(iso);
export function av(n){ return (n||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2); }

// ─── Riusabile: pannelli help (Carrelli, Popup, …) ───
export function LegendInfoIcon({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 16v-4M12 8h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z" />
    </svg>
  );
}

export function useMediaMinWidth(px) {
  const query = `(min-width: ${px}px)`;
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

// ─── Riusabile: CopyBtn ───
export function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="ch-btn ch-btn-sm"
      style={{fontSize:12, marginLeft:8, padding:'2px 8px', color: copied?'#059669':undefined, borderColor: copied?'#bbf7d0':undefined, background: copied?'#f0fdf4':undefined, flexShrink:0}}
      onClick={()=>{ navigator.clipboard.writeText(text); setCopied(true); setTimeout(()=>setCopied(false),2000); }}
    ><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{copied ? Ic.check : Ic.copy}</span></button>
  );
}
